import { useEffect, useState } from 'react'
import { api } from '../services/api'

export interface VersionInfo {
  readonly version: string
  readonly base: string
  readonly gitSha: string
  readonly buildTime: string
}

// __APP_VERSION__ is injected at build time by Vite's `define:` plugin.
// See packages/frontend/vite.config.ts. The fallback handles environments
// where the constant wasn't defined (e.g. raw test runners).
const BUILD_TIME_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown'

const DEFAULT_VERSION: VersionInfo = {
  version: BUILD_TIME_VERSION,
  base: BUILD_TIME_VERSION,
  gitSha: '',
  buildTime: '',
}

// Module-scoped cache: fetched at most once per page load, shared by every
// caller. Mirrors the use-features.ts pattern.
let cached: VersionInfo | null = null
let inflight: Promise<VersionInfo> | null = null

async function load(): Promise<VersionInfo> {
  if (cached) return cached
  if (inflight) return inflight
  inflight = api
    .get<VersionInfo>('/meta/version')
    .then((res) => {
      cached = res.data ?? DEFAULT_VERSION
      return cached
    })
    .catch(() => DEFAULT_VERSION)
    .finally(() => {
      inflight = null
    })
  return inflight
}

/**
 * Returns the build-time and runtime versions.
 *
 *   - `buildTime` is the version that was baked into this SPA bundle when
 *     the frontend was compiled. Constant for the life of the page load.
 *   - `runtime` is the version reported by the API right now. Starts as
 *     `buildTime` and updates after the first fetch resolves.
 *
 * If they differ after the fetch, the user is running a stale cached
 * SPA against a newer backend. The caller (Layout) renders a small
 * click-to-reload chip in that case. See docs/VERSIONING.md.
 */
export function useVersion(): { readonly buildTime: string; readonly runtime: string } {
  // Initial state captures the cached value if a prior caller already
  // fetched it; otherwise it falls back to the build-time constant so
  // the sidebar footer shows something on first paint with no roundtrip.
  const [runtime, setRuntime] = useState<string>(cached?.version ?? BUILD_TIME_VERSION)
  useEffect(() => {
    let cancelled = false
    // load() returns the cached value immediately if present, so this
    // is a no-op for any caller after the first one. The setRuntime
    // call is bailed out by React if the value is unchanged.
    load().then((v) => {
      if (!cancelled) setRuntime(v.version)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return { buildTime: BUILD_TIME_VERSION, runtime }
}
